import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Send } from "lucide-react";
import { publicApi, photoUrl, useAccount, errMsg } from "@/lib/account";
import { ALL_COUNTRIES } from "@/lib/countries";
import useSeo from "@/lib/seo";

const ShareForm = () => {
  const { isSignedIn, authed } = useAccount();
  const [f, setF] = useState({ couple: "", story: "", country: "", year: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!isSignedIn) return <Link to="/sign-in" className="btn-gold inline-block px-6 py-3 rounded-full text-sm" data-testid="story-signin-hint">Sign in to share your story</Link>;
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append("photo", photo);
    setBusy(true);
    try { await authed("post", "/stories", fd); toast.success("Thank you! Your story will appear after review."); setF({ couple: "", story: "", country: "", year: "" }); setPhoto(null); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const on = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="story-form">
      <input className="field" placeholder="Your names, e.g. Ana & Rohan" required minLength={3} value={f.couple} onChange={on("couple")} data-testid="story-couple-input" />
      <div className="grid grid-cols-2 gap-3">
        <select className="field" value={f.country} onChange={on("country")} data-testid="story-country-select"><option value="">Country</option>{ALL_COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select>
        <input className="field" placeholder="Wedding year" maxLength={4} value={f.year} onChange={on("year")} data-testid="story-year-input" />
      </div>
      <textarea className="field sm:col-span-2 min-h-[140px]" placeholder="How did you meet? What made you say yes?" required minLength={30} maxLength={3000} value={f.story} onChange={on("story")} data-testid="story-text-input" />
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="text-sm text-slate-400" data-testid="story-photo-input" />
      <button disabled={busy} className="btn-gold px-6 py-3 rounded-full flex items-center gap-2 justify-center" data-testid="story-submit-button"><Send className="w-4 h-4" /> {busy ? "Sending..." : "Share our story"}</button>
    </form>
  );
};

export default function Stories() {
  const [items, setItems] = useState([]);
  useSeo("Success Stories – Couples Who Met on Vivaah Royale", "Real love stories from couples across cultures and countries who met on Vivaah Royale.");
  useEffect(() => { publicApi.get("/stories").then((r) => setItems(r.data)); }, []);
  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14" data-testid="stories-page">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Success stories</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Love that found its way</h1>
      <div className="grid md:grid-cols-2 gap-6 mt-12">
        {items.length === 0 && <p className="text-slate-400 md:col-span-2" data-testid="stories-empty">Our community is just beginning — be the first couple to share your story.</p>}
        {items.map((s) => (
          <article key={s.id} className="card-royal rounded-3xl overflow-hidden" data-testid={`story-${s.id}`}>
            {s.photo && <img src={photoUrl(s.photo)} alt={s.couple} className="w-full h-64 object-cover" loading="lazy" />}
            <div className="p-7">
              <h2 className="font-display text-2xl flex items-center gap-2"><Heart className="w-5 h-5 text-rose-400" fill="currentColor" /> {s.couple}</h2>
              <p className="text-xs text-slate-400 mt-1">{[s.country, s.year && `Married ${s.year}`].filter(Boolean).join(" · ")}</p>
              <p className="text-slate-300 mt-4 leading-relaxed whitespace-pre-line">{s.story}</p>
            </div>
          </article>
        ))}
      </div>
      <section className="card-royal rounded-3xl p-8 mt-14" data-testid="story-share-section">
        <h2 className="font-display text-3xl">Met your partner here?</h2>
        <p className="text-sm text-slate-400 mt-2 mb-6">Share your story to inspire others. We publish it after a quick review.</p>
        <ShareForm />
      </section>
    </div>
  );
}
